import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AssetStrip from '@/components/AssetStrip';
import BannerAdSlot from '@/components/BannerAdSlot';
import BuiltForYouSection from '@/components/BuiltForYouSection';
import CopySection from '@/components/CopySection';
import CountdownTimer from '@/components/CountdownTimer';
import FinalCTA from '@/components/FinalCTA';
import HeroSection from '@/components/HeroSection';
import RotatorPoolVisual from '@/components/RotatorPoolVisual';
import SignupStats from '@/components/SignupStats';
import SiteFooter from '@/components/SiteFooter';
import SiteHeader from '@/components/SiteHeader';

// Reserved system paths that must NEVER be treated as a referral handle.
// Any of these collide with real app routes registered in App.js; if the
// router happens to reach Home with one of them as :ref (e.g. via a
// wildcard or stale URL), we silently ignore it.
const RESERVED_PATHS = new Set([
  'login',
  'signup',
  'dashboard',
  'admin',
  'forgot-password',
  'reset-password',
  'api',
  'banners',
  'logo',
  'static',
  'assets',
  'favicon.ico',
  'robots.txt',
]);

// Defensive validator — referral handles must look like a username.
// (We don't hit the API to verify here — Home stays static; SignupPage
// will pill-validate the code on the next step.)
const _isLikelyHandle = (s) => typeof s === 'string' && s.length >= 2 && s.length <= 64 && /^[a-zA-Z0-9_.-]+$/.test(s);

const Home = () => {
  const { ref } = useParams();

  // Resolve the referral handle if present, otherwise null.
  // Reserved app paths are filtered out so /login etc. never get
  // mistaken for a referral when reached unconventionally.
  const referralCode = useMemo(() => {
    if (!ref) return null;
    if (RESERVED_PATHS.has(ref.toLowerCase())) return null;
    if (!_isLikelyHandle(ref)) return null;
    return ref;
  }, [ref]);

  return (
    <main className="sc-canvas" data-testid="home-page">
      <SiteHeader />

      {/* "Referred by @username" pill — only rendered when the visitor
          arrived via a personal invite link. Subtle, gold-accent, sits
          directly under the header so it's visible on first scroll. */}
      {referralCode ? (
        <div className="px-4 md:px-8 pt-3" data-testid="home-referral-pill-wrap">
          <div className="max-w-[960px] mx-auto">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11.5px] uppercase tracking-[0.18em] font-bold"
              style={{
                background: 'rgba(255, 182, 18, 0.08)',
                border: '1px solid rgba(255, 182, 18, 0.42)',
                color: '#FFC20E',
              }}
              data-testid="home-referral-pill"
            >
              <span style={{ color: '#9c9c9c', fontWeight: 600 }}>Referred by</span>
              <span data-testid="home-referral-pill-username">@{referralCode}</span>
            </div>
          </div>
        </div>
      ) : null}

      {/* Sponsored banners — under the header, above the hero. Pure-random
          rotation per page render. Fully self-managed component. */}
      <div className="px-4 md:px-8 pt-3">
        <BannerAdSlot slot="home" count={2} />
      </div>

      {/* Pre-Launch countdown — renders only when admin has enabled
          pre-launch mode. Self-fetches /api/public/launch-status and
          re-checks every 30 s. Returns null when disabled, so it has
          zero visual footprint in normal operation. */}
      <CountdownTimer />

      {/* Intro video — pinned at the very top of the hero column, directly
          below the banner stack. No autoplay, no mute. Responsive 16:9 frame
          with a brand-matched gold glass border. */}
      <section className="px-4 md:px-8 pt-4 pb-2" data-testid="home-intro-video-section" aria-label="Platform introduction video">
        <div className="max-w-[960px] mx-auto">
          <div
            className="sc-glass rounded-2xl p-2 md:p-3 relative overflow-hidden"
            style={{
              borderColor: 'rgba(255, 182, 18, 0.32)',
              boxShadow: '0 0 0 1px rgba(255,182,18,0.10), 0 12px 40px rgba(0,0,0,0.55)',
            }}
          >
            <div
              className="absolute -top-20 -right-20 w-64 h-64 pointer-events-none rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
              }}
              aria-hidden="true"
            />
            <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }}>
              <iframe
                src="https://www.youtube.com/embed/33Bn7AnmU18?rel=0&playsinline=1"
                title="1SimpleCycler 3.0 — Platform Introduction"
                loading="lazy"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                data-testid="home-intro-video-iframe"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  border: '0',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <HeroSection referralCode={referralCode} />
        <SignupStats />
      </section>

      <AssetStrip />

      <CopySection />

      <BuiltForYouSection />

      <RotatorPoolVisual />

      <FinalCTA referralCode={referralCode} />

      <SiteFooter />
    </main>
  );
};

export default Home;
